import every from "lodash/every";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";

import type { NotificationSettingsOverviewCardProps } from "@/ui/components/profile/NotificationSettingsOverviewCard";
import type { UserOverviewCardProps } from "@/ui/components/profile/UserOverviewCard";
import { getUsStateFullName } from "@/utils/api/enums.util";
import { isNotNilNotEmpty } from "@/utils/shared.utils";
import {
  formatAddress,
  getNotificationSettings,
  getPreferredFullName,
} from "@/utils/user.util";

type UserProfileProps = NotificationSettingsOverviewCardProps &
  Omit<UserOverviewCardProps, "analyticsSource">;

const isAddressStateOnly = (address?: GooglePlacesAddress) => {
  if (!isNotNilNotEmpty(address)) return false;
  const { addressLine1, addressLine2, city, state, zip } = address;

  const isStateEmpty = !isNotNilNotEmpty(state);
  const isEveryOtherFieldEmpty = every(
    [addressLine1, addressLine2, city, zip],
    isEmpty,
  );
  return !isStateEmpty && isEveryOtherFieldEmpty;
};

const getAddress = ({
  fullAddress,
  usStates,
}: {
  fullAddress?: GooglePlacesAddress;
  usStates?: UsStateEnumOption[];
}) => {
  if (isEmpty(fullAddress)) return "";

  if (!isNil(fullAddress.state) && isAddressStateOnly(fullAddress)) {
    return getUsStateFullName({
      abbreviation: fullAddress.state,
      usStates,
    });
  }
  return formatAddress({ address: fullAddress });
};

const getNotificationPreferences = (data: FullProfileResponse) => {
  const {
    receiveEmails: isEmailEnabled,
    receivePush: isPushEnabled,
    receiveSms: isSmsEnabled,
  } = getNotificationSettings(data);
  return {
    isEmailEnabled,
    isPushEnabled,
    isSmsEnabled,
  };
};

const getUserProfileProps = ({
  profile,
  usStates,
}: {
  profile: FullProfileResponse;
  usStates: undefined | UsStateEnumOption[];
}): UserProfileProps => {
  const {
    address: fullAddress,
    cellPhone: phoneNumber,
    chosenName,
    contactPreferences,
    externalId: selectedUserId,
    firstName,
    gender,
    isPatient,
    languages,
    lastName,
    pronouns,
  } = profile;

  const fullName = getPreferredFullName({
    chosenName,
    firstName,
    lastName,
  });

  const address = getAddress({ fullAddress, usStates });

  return {
    address,
    contactPreferences: contactPreferences?.join(", ") ?? "",
    fullName,
    gender,
    ...getNotificationPreferences(profile),
    languages: languages?.join(", ") ?? "",
    phoneNumber,
    pronouns,
    selectedUserId,
    userType: isPatient ? "patient" : "support",
  };
};

export { getUserProfileProps };
